import { useRef, useState, Fragment, useEffect, useMemo } from "react";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from 'react-router-dom';

//Components
import {
    Grid,
    Typography,
    Container,
    Paper,
    Divider,
    Chip,
    Icon
} from "@mui/material";

import { Button } from "@gpstrackit/react-controls";
import DataViewInfiniteScroll from 'Components/DataViewInfiniteScroll';

//Helpers
import { getDateFormatted, getPMUser, getTransactionsProcessingStatusColor } from 'Core/data/Helpers';

//Actions
import {
    searchEntityRequest,
    transactionsDetailsOpenModal,
    clearRedux,
} from 'Redux/actions';

//Views
import TransactionsDetails from '../../.components/transactionsDetails'
import AdvancedFilters from 'Modules/transactions/views/main/.components/AdvancedFilters';

const TransactionsRequests = (props) => {
    const { messages } = useIntl();
    const dispatch = useDispatch();
    const tableRefTransactions = useRef();
    const navigate = useNavigate()
    const { transactionid } = useParams()

    //Redux States
    const lang = useSelector(state => state.commonRedux.language);
    const pmUsers = useSelector(state => state.generalRedux.pmUsers);
    const user = useSelector(state => state.securityRedux.user);
    const { openModal } = useSelector(state => state.transactionsDetailsRedux);

    //Local states
    const [openPanel, setOpenPanel] = useState(false);
    const [openPanelDetails, setOpenPanelDetails] = useState(false);
    const [firstLoad, setFirstLoad] = useState(false);
    const [search, setSearch] = useState(null);
    const [total, setTotal] = useState(0);
    const [account, setAccount] = useState(null);
    const [type, setType] = useState(null);
    const [status, setStatus] = useState(null);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [columns, setColumns] = useState([
        {
            key: 'view',
            show: true,
            locked: true,
            title: "",
            width: 40,
            icon: true,
            render: (item) => {
                return (
                    <Icon style={{ cursor: "pointer", color: '#1D2734' }} className="toolbar-list-icon fs-20" onClick={() => {
                        dispatch(transactionsDetailsOpenModal(item.id, item.transactionType))
                    }}>
                        remove_red_eye
                    </Icon>
                )
            }
        },
        {
            key: "id",
            field: "id",
            title: messages['transactions_requests_id'],
            width: 350,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            export: true,
            functionString: 'return row?.id',
            code: "transactions_requests_id",
            render: (item) => {
                return <span>{item?.id}</span>
            }
        },
        {
            key: "company_name",
            field: "company { id name }",
            title: messages['transactions_requests_company_name'],
            width: 200,
            show: true,
            sort: true,
            resize: true,
            export: true,
            functionString: 'return row?.company?.name',
            code: "transactions_requests_company_name",
            render: (item) => {
                return <span>{item?.company?.name}</span>
            }
        },
        {
            key: "transactionType",
            field: "transactionType",
            title: messages['transactions_requests_type'],
            width: 300,
            show: true,
            sort: true,
            resize: true,
            export: true,
            functionString: 'return row?.transactionType',
            code: "transactions_requests_type",
            render: (item) => {
                return <span>{item?.transactionType}</span>
            }
        },
        {
            key: "projectId",
            field: "projectId",
            title: messages['transactions_requests_project_id'],
            width: 200,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            export: true,
            functionString: 'return row?.projectId',
            code: "transactions_requests_project_id",
            render: (item) => {
                return <span>{item?.projectId}</span>
            }
        },
        {
            key: "processingStatus",
            field: "processingStatus",
            title: messages['transactions_requests_processing_status'],
            width: 150,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            export: true,
            functionString: 'return row?.processingStatus',
            code: "transactions_requests_processing_status",
            render: (item) => {
                return <Chip style={{
                    backgroundColor: getTransactionsProcessingStatusColor(item?.processingStatus).color,
                    color: "#ffffff"
                }} label={messages[getTransactionsProcessingStatusColor(item?.processingStatus).label]} />
            }
        },
        {
            key: "createdOn",
            field: "createdOn",
            title: messages['transactions_requests_created'],
            width: 250,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            export: true,
            functionString: 'return row?.createdOn && (new Date(`${row?.createdOn}`)).toISOString().slice(0, 19).replace("T", " ")',
            code: "transactions_requests_created",
            render: (item) => {
                return <span>{item?.createdOn && getDateFormatted(item?.createdOn, "date", "datetime")}</span>
            }
        },
        {
            key: "createdBy",
            field: "createdBy",
            title: messages['transactions_requests_created_by'],
            show: true,
            resize: true,
            width: 200,
            export: true,
            functionString: `
            
                let pmUsers = ${JSON.stringify(pmUsers)}
                pmUsers = pmUsers.find((item) => ("PM_"+item.id) === row?.createdBy)
                return pmUsers.firstName + " " + pmUsers.lastName || row?.createdBy
            
        `,
            code: "transactions_requests_created_by",
            render: (item) => {
                return <span>{item?.createdBy && getPMUser(item?.createdBy, pmUsers)}</span>
            }
        },
        {
            key: "updatedOn",
            field: "updatedOn",
            title: messages['transactions_requests_updated'],
            width: 250,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            export: true,
            functionString: 'return row?.updatedOn && (new Date(`${row?.updatedOn}`)).toISOString().slice(0, 19).replace("T", " ")',
            code: "transactions_requests_updated",
            render: (item) => {
                return <span>{item?.updatedOn && getDateFormatted(item?.updatedOn, "date", "datetime")}</span>
            }
        }
    ])

    useEffect(() => {
        if (transactionid !== "-1") {
            setSearch(transactionid);
        }
    }, [transactionid])

    useEffect(() => {
        if (search || search === "") {
            const timer = setTimeout(() => {

                let currentConditions = tableRefTransactions?.current?.conditions;
                currentConditions = currentConditions?.filter((item) => item?.fields[0] !== 'id');

                if (search !== "")
                    currentConditions.push({ fields: ["id"], terms: [search], exact: true });

                //Add
                tableRefTransactions?.current?.setTags([{ id: search }]);

                tableRefTransactions?.current?.getData({ newConditions: currentConditions });
            }, 500)

            setFirstLoad(false);
            return () => clearTimeout(timer);
        }
    }, [search])

    useEffect(() => {
        if (transactionid) {
            setFirstLoad(true);
        }
    }, [])

    useEffect(() => {
        if (lang) {
            // Use functional updater to preserve preference-restored 'show' flags
            setColumns(prev => prev.map((item) => ({
                ...item,
                title: messages[item.code],
                render: item.key === "execute" ? (item) => {
                    return (
                        <Fragment>
                            <Button
                                className="btn-dark-blue"
                                onClick={() => {
                                    navigate(`/app/reports/overview/${item.id}`);
                                }}
                            >
                                {messages['transactions_requests_execute']}
                            </Button>
                        </Fragment>
                    )
                } : item.render
            })))
        }
    }, [lang])

    useEffect(() => {
        pmUsers && pmUsers.length === 0 && dispatch(searchEntityRequest({ key: "pmUsers" }));
    }, []);

    // ═══════════════════════════════════════════════════════════════════════════
    // DATAVIEW CONFIGURATION OBJECTS
    // Extracted from JSX for readability
    // ═══════════════════════════════════════════════════════════════════════════

    /**
     * GraphQL configuration for transactions requests
     */
    const graphqlConfig = useMemo(() => ({
        function: "searchTransactionRequests",
        sortType: "TransactionRequestSortInput",
        conditionsType: "TransactionRequestSearchInput",
        dynamicQuery: true,
    }), []);

    /**
     * Search bar configuration
     */
    const searchConfig = useMemo(() => ({
        conditions: ["id", "processingStatus", "transactionType", "projectId"],
        fields: ["id", "processingStatus", "transactionType", "projectId"],
        extraField: "processingStatus transactionType projectId",
        optionValue: "id",
        optionLabel: "id",
        openCustomOptions: (option) => {
            if(!option.projectId){
                return option?.label || option.id
            }
            return `${option.id} | ${option.projectId || ''} - ${option.transactionType || ''}`
        },
    }), []);

    /**
     * Export configuration
     */
    const exportConfig = useMemo(() => ({
        fileName: "transactionsrequests",
    }), []);

    /**
     * UI/Layout configuration
     */
    const uiConfig = useMemo(() => ({
        hideSplit: true,
        topHeight: 210,
        filterSelected: (account || type || status || startDate),
    }), [account, type, status, startDate]);

    /**
     * Selection configuration - item selection disabled
     */
    const selectionConfig = useMemo(() => ({
        disabled: true,
    }), []);

    /**
     * Handle condition changes from search/filters
     */
    const handleChangeConditions = (tags, conditions, total, newColumns, rows, sort, key, fromPreferences, restoreOnly) => {
        // restoreOnly: data already persisted in redux, just sync UI states — skip getData
        if (restoreOnly) return;

        if (!openModal && !firstLoad) {
            let currentConditions = {}
            let currentTags = {}

            if (!conditions && tags) {
                currentConditions = tableRefTransactions?.current?.conditions;
                currentTags = tags;
            } else {
                currentConditions = conditions || [];
                currentTags = currentConditions?.filter((x) => x?.tags)[0]?.tags || [];
                if (currentTags && currentTags.length > 0) tableRefTransactions?.current?.setTags(currentTags);

                //advanced filters

                let validateAccount = currentConditions && currentConditions?.find((x) => x?.fields && x?.fields[0] === "companyId");
                validateAccount && setAccount(validateAccount.terms[0]);

                let validateDeviceModel = currentConditions && currentConditions?.find((x) => x?.fields && x?.fields[0] === "transactionType");
                validateDeviceModel && setType(validateDeviceModel.terms[0]);

                let validateSimProviderAccount = currentConditions && currentConditions?.find((x) => x?.fields && x?.fields[0] === "processingStatus");
                validateSimProviderAccount && setStatus(validateSimProviderAccount.terms[0]);

                let validateDataRange = currentConditions && currentConditions?.find((x) => x?.range);
                validateDataRange && setStartDate(validateDataRange.range.GTE);
                validateDataRange && setEndDate(validateDataRange.range.LTE);

            }

            currentConditions = currentConditions?.filter((item) => item?.fields[0] !== 'id');

            let tagsById = currentTags.filter((item) => !isNaN(parseInt(item.id)));
            if (tagsById?.length > 0) {
                //New Items
                let currentIds = tagsById.map((item) => (item.id));
                if (currentIds.length > 0) {
                    currentConditions.push({ fields: ["id"], terms: currentIds, exact: true, tags: currentTags, conditionOperator: "OR" });
                }
            }
            let tagsByLabel = currentTags.filter(item => isNaN(parseInt(item.id)))
            if (tagsByLabel.length > 0) {
                let currentLabels = tagsByLabel.map((item) => (item.id))
                if (currentLabels.length > 0) {
                    currentConditions.push({ fields: searchConfig.fields, terms: currentLabels, exact: false, tags: currentTags, conditionOperator: "OR" })
                }
            }

            // FIX: Always forward newSort so preference-restored sort isn't lost in tags-only branch
            if (!conditions && tags) {
                tableRefTransactions?.current?.getData({ newConditions: currentConditions, newColumns, newSort: sort });
            } else {
                tableRefTransactions?.current?.getData({ newRows: rows, newSort: sort, newConditions: currentConditions });
            }
        }
    };

    /**
     * Handle column changes (reorder, resize, visibility)
     */
    const handleChangeColumns = (items) => setColumns(items);

    /**
     * Handle filters button click
     */
    const handleFilters = () => setOpenPanel(!openPanel);

    /**
     * Handle total count changes
     */
    const handleChangeTotal = (total) => setTotal(total);

    return (
        <Container maxWidth={false} className="container-view">
            <Paper elevation={0}>
                <Grid
                    container
                    direction="row"
                    justifyContent="flex-start"
                    alignItems="center"
                    style={{ padding: "14px 18px", borderBottom: "1px solid #e8e8e8", height: '95v' }}
                >
                    <Grid item xs={6}>
                        <Typography variant="h5" gutterBottom component="div">
                            {messages['transactions_requests_title']} {/* ({total}) */}
                        </Typography>
                    </Grid>
                    <div style={{ width: '100%', marginBottom: 10, marginTop: 5 }}>
                        <Divider style={{ backgroundColor: '#999999' }} />
                    </div>
                    <DataViewInfiniteScroll
                        key="pm_transactions_main"
                        ref={tableRefTransactions}
                        columns={columns}
                        name="pm-transactions-requests-list"
                        entity="transactions-requests"
                        infiniteScroll
                        clearOnUnmount={false}
                        graphqlConfig={graphqlConfig}
                        searchConfig={searchConfig}
                        exportConfig={exportConfig}
                        uiConfig={uiConfig}
                        selectionConfig={selectionConfig}
                        onChangeColumns={handleChangeColumns}
                        onFilters={handleFilters}
                        onChangeConditions={handleChangeConditions}
                        tableButtons={["export", 'orderColumn']}
                        onChangeTotal={handleChangeTotal}
                    />
                </Grid>
                {openModal && <TransactionsDetails />}
                {
                    (!openPanelDetails && !openModal) &&
                    <AdvancedFilters
                        setOpenPanel={setOpenPanel}
                        openPanel={openPanel}
                        tableRef={tableRefTransactions}
                        account={account}
                        setAccount={setAccount}
                        type={type}
                        setType={setType}
                        status={status}
                        setStatus={setStatus}
                        startDate={startDate}
                        setStartDate={setStartDate}
                        endDate={endDate}
                        setEndDate={setEndDate}
                    />
                }
            </Paper>
        </Container>
    )
}
export default TransactionsRequests;